import * as React from "react";
import { motion, useMotionValue, useTransform, animate, type PanInfo } from "framer-motion";
import { Check, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { haptic, prefersReducedMotion } from "./utils";

interface SwipeActionWrapperProps {
  enabled: boolean;
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  rightLabel: string;
  leftLabel: string;
  rightTone?: "success" | "info";
  leftTone?: "danger" | "warning";
  children: React.ReactNode;
  thresholdRatio?: number; // 0..1
  className?: string;
}

/**
 * Reusable horizontal swipe primitive. Spring-back below threshold,
 * commits with haptic + callback above. Disabled state renders inert.
 * Reduced motion: replaces animation with instant snap.
 */
export function SwipeActionWrapper({
  enabled, onSwipeRight, onSwipeLeft, rightLabel, leftLabel,
  rightTone = "success", leftTone = "danger",
  children, thresholdRatio = 0.4, className,
}: SwipeActionWrapperProps) {
  const x = useMotionValue(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const reduced = prefersReducedMotion();

  const rightOpacity = useTransform(x, [0, 40, 120], [0, 0.4, 1]);
  const leftOpacity = useTransform(x, [-120, -40, 0], [1, 0.4, 0]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    const w = containerRef.current?.offsetWidth ?? 320;
    const threshold = w * thresholdRatio;
    if (info.offset.x >= threshold && onSwipeRight) {
      haptic("success");
      // Animate off-screen, then snap back (parent will collapse/replace card)
      animate(x, w, { duration: reduced ? 0 : 0.18, ease: [0.4, 0, 0.2, 1] }).then(() => {
        onSwipeRight();
        x.set(0);
      });
    } else if (info.offset.x <= -threshold && onSwipeLeft) {
      haptic("warn");
      animate(x, -w, { duration: reduced ? 0 : 0.18, ease: [0.4, 0, 0.2, 1] }).then(() => {
        onSwipeLeft();
        x.set(0);
      });
    } else {
      haptic("select");
      animate(x, 0, reduced ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 35 });
    }
  };

  if (!enabled) {
    return <div className={cn("relative", className)}>{children}</div>;
  }

  return (
    <div ref={containerRef} className={cn("relative overflow-hidden rounded-biz-card", className)}>
      {/* Left (cancel) gutter */}
      <motion.div
        aria-hidden
        style={{ opacity: leftOpacity }}
        className={cn(
          "pointer-events-none absolute inset-y-0 left-0 right-1/2 flex items-center justify-start px-5",
          leftTone === "danger" ? "bg-biz-danger-subtle text-biz-danger" : "bg-biz-warning-subtle text-biz-warning",
        )}
      >
        <div className="flex items-center gap-2">
          <X size={20} weight="regular" />
          <span className="text-sm font-semibold">{leftLabel}</span>
        </div>
      </motion.div>
      {/* Right (complete) gutter */}
      <motion.div
        aria-hidden
        style={{ opacity: rightOpacity }}
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 left-1/2 flex items-center justify-end px-5",
          rightTone === "success" ? "bg-biz-success-subtle text-biz-success" : "bg-biz-info-subtle text-biz-info",
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{rightLabel}</span>
          <Check size={20} weight="regular" />
        </div>
      </motion.div>

      <motion.div
        drag="x"
        dragConstraints={{ left: -200, right: 200 }}
        dragElastic={0.18}
        style={{ x }}
        onDragEnd={onDragEnd}
        className="relative z-10 touch-pan-y"
      >
        {children}
      </motion.div>
    </div>
  );
}
